import React, { useState, useEffect, useRef } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid, 
  Chip,
  Button, 
  Alert,
  List,
  ListItem,
  CircularProgress,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Skeleton,
  Fade,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  Close as CloseIcon,
  Article as ArticleIcon,
  Info as InfoIcon,
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarTodayIcon,
  HomeRepairService as HomeRepairServiceIcon
} from '@mui/icons-material';
import { useServiceRequestQueries, useNotification, Bid, formatDate, ClientProfile } from 'shared-lib';
import { useVendorContext } from '../context/VendorContextProvider';
import { 
    FileAttachmentUploader,
    ServiceDetailsRenderer,
    QASection
} from '../components';
import { Attachment } from 'shared-lib';
import { getDisplayStatus } from '../utils/serviceRequestUtils';
import { AttachmentGallery } from 'shared-lib';
import {  getFirestore, doc, getDoc } from 'firebase/firestore';

const ServiceRequestDetail: React.FC = () => {
  const renderCountRef = useRef(0);
  
  // Render counter
  renderCountRef.current += 1;
  
  const navigate = useNavigate();
  const location = useLocation();
  const { requestId } = useParams<{ requestId: string }>();
  const { currentVendor } = useVendorContext();
  const { showNotification } = useNotification();
  
  // Store showNotification in a ref to prevent it from causing re-renders
  const showNotificationRef = useRef(showNotification);
  // Update ref when showNotification changes
  useEffect(() => {
    showNotificationRef.current = showNotification;
  }, [showNotification]);
  
  const [bidAmount, setBidAmount] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [bidSubmitted, setBidSubmitted] = useState(false);
  const [bidError, setBidError] = useState('');
  const [insuranceConfirmed, setInsuranceConfirmed] = useState(false);
  
  // Add state for earliest start date and project duration
  const [earliestStartDate, setEarliestStartDate] = useState<Date | null>(null);
  const [projectDuration, setProjectDuration] = useState<string>('');
  
  // Add state for attachments and upload status
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadsInProgress, setUploadsInProgress] = useState(false);
  
  // Refs to prevent notification infinite loops
  const errorShownRef = useRef<boolean>(false);
  const prevBidErrorRef = useRef<string | null>(null);
  const successNotificationShownRef = useRef<boolean>(false);
  const rejectNotificationShownRef = useRef<boolean>(false);
  
  const { 
    useServiceRequest, 
    useBidsByRequest,
    useSubmitBid
  } = useServiceRequestQueries();
  
  const { 
    data: serviceRequest, 
    isLoading: requestLoading, 
    error: requestError 
  } = useServiceRequest(requestId || '');
  
  const {
    data: existingBids = [],
    isLoading: bidsLoading
  } = useBidsByRequest(requestId || '');

  // Check if vendor already submitted a bid or declined
  const vendorBid = currentVendor 
    ? existingBids.find(bid => bid.vendorId === currentVendor.id) as Bid | null
    : null;
  
  // Check if user is authenticated
  const isAuthenticated = !!currentVendor;
  
  const submitBidMutation = useSubmitBid();
  
  const isLoading = requestLoading || bidsLoading;
  const error = requestError;
  
  const handleBack = () => {
    navigate(-1);
  };

  // Handle file uploads
  const handleFilesUpload = (newAttachments: Attachment[]) => {
    if (Array.isArray(newAttachments)) {
      setAttachments(newAttachments);
    }
  };
  
  // Handle upload status change
  const handleUploadStatusChange = (isUploading: boolean) => {
    setUploadsInProgress(isUploading);
  };

  const handleSubmitBid = async () => {
    if (!currentVendor || !requestId) {
      setBidError('No vendor selected or invalid request');
      return;
    }

    // Prevent duplicate submissions
    if (submitBidMutation.isPending || bidSubmitted) {
      return;
    }

    // Check if uploads are still in progress
    if (uploadsInProgress) {
      setBidError('Please wait for all file uploads to complete before submitting');
      return;
    }

    // Check if vendor confirmed they meet insurance requirements
    if (!insuranceConfirmed) {
      setBidError('You must confirm you meet the insurance requirements before submitting');
      return;
    }

    try {
      const amount = parseFloat(bidAmount);
      if (isNaN(amount) || amount <= 0) {
        setBidError('Please enter a valid bid amount');
        return;
      }

      // Validate earliest start date is provided
      if (!earliestStartDate) {
        setBidError('Please specify the earliest start date');
        return;
      }

      const duration = projectDuration ? parseInt(projectDuration, 10) : undefined;
      if (!duration || isNaN(duration) || duration <= 0) {
        setBidError('Please enter a valid project duration in days');
        return;
      }

      await submitBidMutation.mutateAsync({
        requestId,
        vendorId: currentVendor.id,
        intent: 'bid',
        amount,
        notes: notes,
        attachments: attachments,
        earliestStartDate: earliestStartDate,
        projectDuration: duration
      } as any); // Use type assertion for now
      
      setBidSubmitted(true);
      setBidError('');
      
      // Show success notification only once
      if (!successNotificationShownRef.current) {
        successNotificationShownRef.current = true;
        showNotificationRef.current({
          message: `Your bid of $${amount.toFixed(2)} has been submitted successfully.`,
          severity: 'success',
          autoHideDuration: 3000
        });
      }
      
      // Redirect to requests page after a short delay
      setTimeout(() => {
        navigate('/requests');
      }, 1500);
    } catch (error) {
      console.error('Error submitting bid:', error);
      setBidError('Failed to submit bid. Please try again.');
    }
  };

  // Remove the cleanup notification to prevent infinite loops
  useEffect(() => {
    return () => {
      // Just reset refs when component unmounts
      successNotificationShownRef.current = false;
      errorShownRef.current = false;
      rejectNotificationShownRef.current = false;
      prevBidErrorRef.current = null;
    };
  }, []);

  const handleRejectRequest = async () => {
    if (!currentVendor || !requestId) {
      setBidError('No vendor selected or invalid request');
      return;
    }
    
    // Prevent duplicate submissions
    if (submitBidMutation.isPending) {
      return;
    }
    
    try {
      // If the vendor already has a bid with intent='bid', we need to create a new ignore
      // since we don't want to convert a real bid to a ignore
      await submitBidMutation.mutateAsync({
        requestId,
        vendorId: currentVendor.id,
        intent: 'ignore',
        reason: "Request ignored by vendor"
      } as any); // Use type assertion for now
      
      // Show rejection notification only once
      if (!rejectNotificationShownRef.current) {
        rejectNotificationShownRef.current = true;
        showNotificationRef.current({
          message: 'You have ignored this request.',
          severity: 'info',
          autoHideDuration: 3000
        });
      }
      
      // Navigate to requests page
      navigate('/requests');
    } catch (error) {
      console.error('Error ignoring request:', error);
      setBidError('Failed to ignore the request. Please try again.');
    }
  };
  
  // Show error notification if there's an error - with extra safety
  useEffect(() => {
    // Safety check to prevent infinite loops
    if (renderCountRef.current > 25) return;
    
    if (error && !errorShownRef.current) {
      errorShownRef.current = true;
      // Use setTimeout to break potential synchronous cycles
      setTimeout(() => {
        showNotificationRef.current({
          message: 'Error loading service request details. Please try again.',
          severity: 'error'
        });
      }, 0);
    } else if (!error) {
      errorShownRef.current = false;
    }
  }, [error]); // Only depend on error, not on showNotification
  
  // Show bid error notification - with extra safety
  useEffect(() => {
    // Safety check to prevent infinite loops
    if (renderCountRef.current > 25) return;
    
    // Only show notification if bidError is different from previous error
    if (bidError && bidError !== prevBidErrorRef.current) {
      prevBidErrorRef.current = bidError;
      
      // Use setTimeout to break potential synchronous cycles
      setTimeout(() => {
        showNotificationRef.current({
          message: bidError,
          severity: 'error'
        });
      }, 0);
    }
  }, [bidError]); // Only depend on bidError, not on showNotification
  
  // Helper function to display service details based on type
  const renderServiceDetails = () => {
    if (!serviceRequest || !serviceRequest.serviceDetails) {
      return null;
    }

    return <ServiceDetailsRenderer serviceDetails={serviceRequest.serviceDetails} />;
  };
  
  const [clientProfile, setClientProfile] = useState<ClientProfile | null>(null);
  
  // Fetch client information if bid is accepted
  useEffect(() => {
    const fetchClientProfile = async () => {
      if (!serviceRequest) return;
      
      // Always fetch client profile for insurance requirements
      try {
        const db = getFirestore();
        const clientRef = doc(db, 'clients', serviceRequest.clientId);
        const clientDoc = await getDoc(clientRef);
        
        if (clientDoc.exists()) {
          setClientProfile({
            id: clientDoc.id,
            ...clientDoc.data()
          } as ClientProfile);
        }
      } catch (error) {
        console.error('Error fetching client profile:', error);
      }
    };
    
    fetchClientProfile();
  }, [serviceRequest]);
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error || !serviceRequest) {
    return (
      <Box sx={{ my: 4 }}>
        <Typography variant="h5">Error Loading Request</Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          There was an error loading the service request details. Please try again later.
        </Typography>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Back to Requests
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto', px: { xs: 1, sm: 3 } }}>      
      {isLoading ? (
        <Box sx={{ my: { xs: 2, sm: 4 } }}>
          <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2 }} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 4 }}>
          An error occurred loading the request details. Please try again.
        </Alert>
      ) : !serviceRequest ? (
        <Alert severity="warning" sx={{ mb: 4 }}>
          Request not found.
        </Alert>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={8}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                borderRadius: '16px', 
                border: '1px solid rgba(16, 29, 59, 0.15)',
                mb: { xs: 2, sm: 3 },
                boxShadow: '0px 4px 20px -4px rgba(16, 29, 59, 0.08)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Service request details section */}
              <Fade in={true} timeout={500}>
                <Box>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: { xs: 2, sm: 3 }
                  }}>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#101D3B'
                      }}
                    >
                      {serviceRequest.serviceType} Service Request
                    </Typography>
                    
                    <Chip 
                      icon={
                        serviceRequest.status === 'active' 
                          ? <AccessTimeIcon fontSize="small" /> 
                          : serviceRequest.status === 'closed' 
                            ? <CheckCircleIcon fontSize="small" />
                            : <CloseIcon fontSize="small" />
                      }
                      label={serviceRequest.status.charAt(0).toUpperCase() + serviceRequest.status.slice(1)} 
                      color={serviceRequest.status === 'active' ? 'info' : serviceRequest.status === 'closed' ? 'success' : 'error'}
                      size="medium"
                      sx={{ 
                        fontWeight: 600,
                        px: 1
                      }}
                    />
                  </Box>
                  
                  <Card
                    elevation={0}
                    sx={{
                      border: '1px solid rgba(16, 29, 59, 0.08)',
                      borderRadius: '12px',
                      bgcolor: 'rgba(16, 29, 59, 0.02)'
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>                    
                      <List disablePadding>
                        <ListItem 
                          disablePadding 
                          sx={{ 
                            mb: { xs: 1, sm: 2 },
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'flex-start' }
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            width: { sm: '180px' },
                            flexShrink: 0,
                            mb: { xs: 0.5, sm: 0 }
                          }}>
                            <LocationOnIcon sx={{ color: 'rgba(16, 29, 59, 0.6)', mr: 1, fontSize: '1.2rem' }} />
                            <Typography 
                              variant="body2" 
                              component="span"
                              sx={{ 
                                fontWeight: 600,
                                color: 'rgba(16, 29, 59, 0.7)',
                              }}
                            >
                              Location:
                            </Typography>
                          </Box>
                          <Typography variant="body1" component="span" sx={{ color: '#101D3B', fontWeight: 500 }}>
                            {serviceRequest.address}, {serviceRequest.city}, {serviceRequest.state} {serviceRequest.zipCode}
                          </Typography>
                        </ListItem>
                        
                        <ListItem 
                          disablePadding 
                          sx={{ 
                            mb: { xs: 1, sm: 2 },
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'flex-start' }
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            width: { sm: '180px' },
                            flexShrink: 0,
                            mb: { xs: 0.5, sm: 0 }
                          }}>
                            <CalendarTodayIcon sx={{ color: 'rgba(16, 29, 59, 0.6)', mr: 1, fontSize: '1.2rem' }} />
                            <Typography 
                              variant="body2" 
                              component="span"
                              sx={{ 
                                fontWeight: 600,
                                color: 'rgba(16, 29, 59, 0.7)',
                              }}
                            >
                              Timeline:
                            </Typography>
                          </Box>
                          <Typography variant="body1" component="span" sx={{ color: '#101D3B', fontWeight: 500 }}>
                            {serviceRequest.projectTimeline}
                            {serviceRequest.preferredDate && (
                              <Chip 
                                size="small" 
                                label={`Preferred: ${formatDate(serviceRequest.preferredDate)}`}
                                sx={{ ml: 1, bgcolor: 'rgba(0, 171, 155, 0.1)', color: '#00AB9B', fontWeight: 500 }}
                              />
                            )}
                          </Typography>
                        </ListItem>
                        
                        <ListItem 
                          disablePadding 
                          sx={{ 
                            mb: { xs: 1, sm: 2 },
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'flex-start' }
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            width: { sm: '180px' },
                            flexShrink: 0,
                            mb: { xs: 0.5, sm: 0 }
                          }}>
                            <HomeRepairServiceIcon sx={{ color: 'rgba(16, 29, 59, 0.6)', mr: 1, fontSize: '1.2rem' }} />
                            <Typography 
                              variant="body2" 
                              component="span"
                              sx={{ 
                                fontWeight: 600,
                                color: 'rgba(16, 29, 59, 0.7)',
                              }}
                            >
                              Service:
                            </Typography>
                          </Box>
                          <Typography variant="body1" component="span" sx={{ color: '#101D3B', fontWeight: 500 }}>
                            {serviceRequest.serviceName || serviceRequest.serviceType}
                          </Typography>
                        </ListItem>
                        
                        {/* Add service details here */}
                        {serviceRequest.serviceName && serviceRequest.serviceDetails && (
                          <ListItem 
                            disablePadding 
                            sx={{ 
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start'
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              mb: 1.5,
                              width: '100%',
                              borderBottom: '1px solid rgba(16, 29, 59, 0.08)',
                              pb: 1
                            }}>
                              <ArticleIcon sx={{ color: 'rgba(16, 29, 59, 0.6)', mr: 1, fontSize: '1.2rem' }} />
                              <Typography 
                                variant="body2" 
                                component="span"
                                sx={{ 
                                  fontWeight: 600,
                                  color: 'rgba(16, 29, 59, 0.7)',
                                }}
                              >
                                Service Details:
                              </Typography>
                            </Box>
                            <Box 
                              sx={{ 
                                p: 2,
                                bgcolor: 'rgba(16, 29, 59, 0.03)',
                                borderRadius: '8px',
                                width: '100%',
                                border: '1px solid rgba(16, 29, 59, 0.06)'
                              }}
                            >
                              {renderServiceDetails()}
                            </Box>
                          </ListItem>
                        )}
                        
                        {/* Insurance Requirements - visible to all vendors */}
                        {clientProfile && (
                          <ListItem 
                            disablePadding 
                            sx={{ 
                              mt: { xs: 2, sm: 2 },
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start'
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              mb: 1.5,
                              width: '100%',
                              borderBottom: '1px solid rgba(16, 29, 59, 0.08)',
                              pb: 1
                            }}>
                              <ArticleIcon sx={{ color: 'rgba(16, 29, 59, 0.6)', mr: 1, fontSize: '1.2rem' }} />
                              <Typography 
                                variant="body2" 
                                component="span"
                                sx={{ 
                                  fontWeight: 600,
                                  color: 'rgba(16, 29, 59, 0.7)',
                                }}
                              >
                                Insurance Requirements for Working on Site:
                              </Typography>
                            </Box>
                            <Box 
                              sx={{ 
                                p: 2,
                                bgcolor: 'rgba(0, 171, 155, 0.03)',
                                borderRadius: '8px',
                                width: '100%',
                                border: '1px solid rgba(0, 171, 155, 0.1)'
                              }}
                            >
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" sx={{ mb: { xs: 0.3, sm: 0.5 } }}>
                                    <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>
                                      Commercial General Liability:
                                    </Box> 
                                    {clientProfile.commercialGeneralLiabilityAmount 
                                      ? `$${clientProfile.commercialGeneralLiabilityAmount.toLocaleString()}`
                                      : 'Not specified'}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: { xs: 0.3, sm: 0.5 } }}>
                                    <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>
                                      Workers Compensation:
                                    </Box> 
                                    {clientProfile.workersCompensationAmount 
                                      ? `$${clientProfile.workersCompensationAmount.toLocaleString()}`
                                      : 'Not specified'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="body2" sx={{ mb: { xs: 0.3, sm: 0.5 } }}>
                                    <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>
                                      Automobile Liability:
                                    </Box> 
                                    {clientProfile.automobileLiabilityAmount 
                                      ? `$${clientProfile.automobileLiabilityAmount.toLocaleString()}`
                                      : 'Not specified'}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: { xs: 0.3, sm: 0.5 } }}>
                                    <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>
                                      Umbrella:
                                    </Box> 
                                    {clientProfile.umbrellaAmount 
                                      ? `$${clientProfile.umbrellaAmount.toLocaleString()}`
                                      : 'Not specified'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </ListItem>
                        )}
                        
                        {/* Bidding End Date - specific rendering for active requests */}
                        {getDisplayStatus(vendorBid) === 'no-bid' && (
                          <ListItem 
                            disablePadding 
                            sx={{ 
                              mt: { xs: 2, sm: 2 },
                              pt: { xs: 2, sm: 2 },
                              borderTop: '1px dashed rgba(16, 29, 59, 0.1)',
                              display: 'flex',
                              flexDirection: { xs: 'column', sm: 'row' },
                              alignItems: { xs: 'flex-start', sm: 'center' }
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              width: { sm: '180px' },
                              flexShrink: 0,
                              mb: { xs: 0.5, sm: 0 }
                            }}>
                              <AccessTimeIcon sx={{ color: '#f57c00', mr: 1, fontSize: '1.2rem' }} />
                              <Typography 
                                variant="body2" 
                                component="span"
                                sx={{ 
                                  fontWeight: 600,
                                  color: '#f57c00'
                                }}
                              >
                                Bidding closes:
                              </Typography>
                            </Box>
                            <Typography 
                              variant="body2" 
                              component="span" 
                              sx={{ 
                                color: '#f57c00', 
                                fontWeight: 500,
                                backgroundColor: 'rgba(245, 124, 0, 0.05)',
                                py: { xs: 0.5, sm: 0.5 },
                                px: { xs: 1, sm: 1 },
                                borderRadius: '4px'
                              }}
                            >
                              {(() => {
                                try {
                                  if (!serviceRequest.biddingEndDate) return 'Not specified';
                                  return formatDate(serviceRequest.biddingEndDate);
                                } catch (e) {
                                  console.error('Error formatting bidding end date:', e);
                                  return 'Not specified';
                                }
                              })()}
                            </Typography>
                          </ListItem>
                        )}
                        
                        <ListItem 
                          disablePadding 
                          sx={{ 
                            mt: { xs: 2, sm: 2 },
                            pt: { xs: 2, sm: 2 },
                            borderTop: '1px dashed rgba(16, 29, 59, 0.1)',
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' }
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            width: { sm: '180px' },
                            flexShrink: 0,
                            mb: { xs: 0.5, sm: 0 }
                          }}>
                            <InfoIcon sx={{ color: 'rgba(16, 29, 59, 0.6)', mr: 1, fontSize: '1.2rem' }} />
                            <Typography 
                              variant="body2" 
                              component="span"
                              sx={{ 
                                fontWeight: 600,
                                color: 'rgba(16, 29, 59, 0.7)',
                              }}
                            >
                              Created:
                            </Typography>
                          </Box>
                          <Typography variant="body2" component="span" sx={{ color: 'rgba(16, 29, 59, 0.7)' }}>
                            {formatDate(serviceRequest.createdAt)}
                          </Typography>
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>

                  {/* Q&A Section */}
                  {serviceRequest && serviceRequest.status === 'active' && (
                    <QASection requestId={serviceRequest.id} />
                  )}
                </Box>
              </Fade>
            </Paper>
          </Grid>
          
          {/* Only show bid section if user is authenticated */}
          {isAuthenticated ? (
            <Grid item xs={12} md={4}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: { xs: 2, sm: 3 }, 
                  borderRadius: '16px', 
                  border: '1px solid rgba(16, 29, 59, 0.15)',
                  boxShadow: '0px 4px 20px -4px rgba(16, 29, 59, 0.08)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    mb: { xs: 1, sm: 2 },
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {vendorBid && vendorBid.intent === 'bid' ? (
                    <>
                      <CheckCircleIcon sx={{ mr: 1, color: '#00AB9B' }} />
                      Your Bid
                    </>
                  ) : (
                    <>
                      <AttachMoneyIcon sx={{ mr: 1, color: '#00AB9B' }} />
                      Bid on this Request
                    </>
                  )}
                </Typography>
                
                {bidError && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: { xs: 2, sm: 2 },
                      borderRadius: '8px',
                      '& .MuiAlert-icon': {
                        alignItems: 'center'
                      }
                    }}
                  >
                    {bidError}
                  </Alert>
                )}
                
                {bidSubmitted || vendorBid ? (
                  <Box>
                    {vendorBid && vendorBid.intent === 'ignore' ? (
                      <Box sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        bgcolor: 'rgba(211, 47, 47, 0.05)',
                        border: '1px solid rgba(211, 47, 47, 0.2)',
                        borderRadius: '12px', 
                        color: '#d32f2f',
                        mb: { xs: 1, sm: 2 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start'
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#d32f2f' }}>
                          Request Ignored
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(211, 47, 47, 0.8)' }}>
                          You have chosen not to bid on this service request.
                        </Typography>
                        {vendorBid && vendorBid.reason && (
                          <Box sx={{ mt: { xs: 1, sm: 2 }, width: '100%' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'rgba(211, 47, 47, 0.8)' }}>
                              Your reason:
                            </Typography>
                            <Box sx={{ 
                              p: { xs: 2, sm: 2 }, 
                              mt: { xs: 1, sm: 1 }, 
                              bgcolor: 'rgba(211, 47, 47, 0.03)', 
                              borderRadius: '8px',
                              border: '1px solid rgba(211, 47, 47, 0.08)',
                              color: 'rgba(211, 47, 47, 0.8)'
                            }}>
                              <Typography variant="body2">
                                {vendorBid.reason}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        
                        {/* Allow submitting a bid after declining */}
                        <Box sx={{ mt: { xs: 1, sm: 2 }, width: '100%' }}>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => setBidSubmitted(false)}
                            sx={{
                              color: '#00AB9B',
                              borderColor: '#00AB9B',
                              borderRadius: '8px',
                              fontWeight: 600,
                              '&:hover': {
                                borderColor: '#00AB9B',
                                bgcolor: 'rgba(0, 171, 155, 0.04)'
                              }
                            }}
                          >
                            Submit a Bid Instead
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        bgcolor: 'rgba(0, 171, 155, 0.05)',
                        border: '1px solid rgba(0, 171, 155, 0.2)',
                        borderRadius: '12px', 
                        color: '#00AB9B',
                        mb: { xs: 1, sm: 2 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start'
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#00AB9B' }}>
                          Bid Submitted
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#00AB9B' }}>
                          Your bid of <Box component="span" sx={{ fontWeight: 700 }}>${vendorBid?.amount?.toFixed(2) || parseFloat(bidAmount).toFixed(2)}</Box> has been submitted.
                        </Typography>
                        
                        {/* Show project timing details if they exist */}
                        {(vendorBid?.earliestStartDate || vendorBid?.projectDuration) && (
                          <Box sx={{ mt: 1, color: '#00AB9B' }}>
                            {vendorBid?.earliestStartDate && (
                              <Typography variant="body2">
                                Earliest start: <Box component="span" sx={{ fontWeight: 700 }}>
                                  {new Date(vendorBid.earliestStartDate).toLocaleDateString()}
                                </Box>
                              </Typography>
                            )}
                            
                            {vendorBid?.projectDuration && (
                              <Typography variant="body2">
                                Project duration: <Box component="span" sx={{ fontWeight: 700 }}>
                                  {vendorBid.projectDuration} days
                                </Box>
                              </Typography>
                            )}
                          </Box>
                        )}
                        
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mt: { xs: 1, sm: 2 },
                          px: { xs: 2, sm: 2 },
                          py: { xs: 1, sm: 1 },
                          borderRadius: '8px',
                          bgcolor: 'rgba(0, 171, 155, 0.05)',
                          border: '1px solid rgba(0, 171, 155, 0.1)',
                          width: '100%'
                        }}>
                          <Box sx={{ 
                            width: 10, 
                            height: 10, 
                            borderRadius: '50%', 
                            bgcolor: vendorBid?.status === 'accepted' ? '#00AB9B' : 
                                    vendorBid?.status === 'rejected' ? '#d32f2f' : '#f57c00',
                            mr: 1
                          }} />
                          <Typography variant="body2" sx={{ 
                            fontWeight: 600,
                            color: vendorBid?.status === 'accepted' ? '#00AB9B' : 
                                  vendorBid?.status === 'rejected' ? '#d32f2f' : '#f57c00'
                          }}>
                            {vendorBid?.status === 'accepted' 
                              ? 'Won - Your bid has been accepted!' 
                              : vendorBid?.status === 'rejected'
                              ? 'Not Selected - Client chose another vendor'
                              : 'Sent - Waiting for client review'}
                          </Typography>
                        </Box>
                        
                        {/* Display client information when bid is accepted */}
                        {vendorBid?.status === 'accepted' && clientProfile && (
                          <Box sx={{
                            mt: { xs: 2, sm: 3 },
                            p: { xs: 1.5, sm: 2 },
                            borderRadius: '8px',
                            bgcolor: 'rgba(0, 171, 155, 0.1)',
                            border: '1px solid rgba(0, 171, 155, 0.2)',
                            width: '100%'
                          }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#00AB9B' }}>
                              Client Information
                            </Typography>
                            <Typography variant="body2" sx={{ mb: { xs: 0.3, sm: 0.5 } }}>
                              <strong>Company:</strong> {clientProfile.companyName}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: { xs: 0.3, sm: 0.5 } }}>
                              <strong>Contact:</strong> {clientProfile.contactFullName}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: { xs: 0.3, sm: 0.5 } }}>
                              <strong>Email:</strong> {clientProfile.contactEmail}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: { xs: 0.3, sm: 0.5 } }}>
                              <strong>Phone:</strong> {clientProfile.contactPhoneNumber}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: { xs: 0.3, sm: 0.5 } }}>
                              <strong>Address:</strong> {clientProfile.address}, {clientProfile.city}, {clientProfile.state} {clientProfile.zipCode}
                            </Typography>
                            
                            {/* Insurance Requirements */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: { xs: 1, sm: 2 }, mb: { xs: 0.3, sm: 0.5 }, color: '#00AB9B' }}>
                              Insurance Requirements
                            </Typography>
                            <Typography variant="body2" sx={{ mb: { xs: 0.3, sm: 0.5 } }}>
                              <strong>Commercial General Liability:</strong> {clientProfile.commercialGeneralLiabilityAmount 
                                ? `$${clientProfile.commercialGeneralLiabilityAmount.toLocaleString()}`
                                : 'Not specified'}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: { xs: 0.3, sm: 0.5 } }}>
                              <strong>Workers Compensation:</strong> {clientProfile.workersCompensationAmount 
                                ? `$${clientProfile.workersCompensationAmount.toLocaleString()}`
                                : 'Not specified'}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: { xs: 0.3, sm: 0.5 } }}>
                              <strong>Automobile Liability:</strong> {clientProfile.automobileLiabilityAmount 
                                ? `$${clientProfile.automobileLiabilityAmount.toLocaleString()}`
                                : 'Not specified'}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: { xs: 0.3, sm: 0.5 } }}>
                              <strong>Umbrella:</strong> {clientProfile.umbrellaAmount 
                                ? `$${clientProfile.umbrellaAmount.toLocaleString()}`
                                : 'Not specified'}
                            </Typography>
                          </Box>
                        )}
                        
                        {vendorBid?.notes && (
                          <Box sx={{ mt: { xs: 1, sm: 2 }, width: '100%' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#00AB9B' }}>
                              Your note:
                            </Typography>
                            <Box sx={{ 
                              p: { xs: 2, sm: 2 }, 
                              mt: { xs: 1, sm: 1 }, 
                              bgcolor: 'rgba(0, 171, 155, 0.03)', 
                              borderRadius: '8px',
                              border: '1px solid rgba(0, 171, 155, 0.08)',
                              color: '#101D3B'
                            }}>
                              <Typography variant="body2">
                                {vendorBid?.notes}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        
                        {/* Display attachments */}
                        {(vendorBid?.attachments && vendorBid.attachments.length > 0) ? (
                          <Box sx={{ mt: { xs: 2, sm: 2 }, width: '100%' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#00AB9B', mb: 1 }}>
                              Your attachments:
                            </Typography>
                            <AttachmentGallery attachments={vendorBid.attachments} />
                          </Box>
                        ) : bidSubmitted && attachments.length > 0 ? (
                          <Box sx={{ mt: { xs: 2, sm: 2 }, width: '100%' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#00AB9B', mb: 1 }}>
                              Your attachments:
                            </Typography>
                            <AttachmentGallery attachments={attachments} />
                          </Box>
                        ) : null}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmitBid(); }}>
                    <TextField
                      label="Bid Amount"
                      fullWidth
                      type="number"
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoneyIcon sx={{ color: '#00AB9B' }} />
                          </InputAdornment>
                        ),
                      }}
                      margin="normal"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          '& fieldset': {
                            borderColor: 'rgba(16, 29, 59, 0.2)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(16, 29, 59, 0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#00AB9B',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#00AB9B',
                        },
                      }}
                      helperText="Enter your total bid amount in USD"
                      required
                    />
                    

                    
                    {/* Add earliest start date field */}
                    <TextField
                      label="Earliest Start Date"
                      fullWidth
                      type="date"
                      value={earliestStartDate ? earliestStartDate.toISOString().split('T')[0] : ''}
                      onChange={(e) => {
                        const dateStr = e.target.value;
                        setEarliestStartDate(dateStr ? new Date(dateStr) : null);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      margin="normal"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          '& fieldset': {
                            borderColor: 'rgba(16, 29, 59, 0.2)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(16, 29, 59, 0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#00AB9B',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#00AB9B',
                        },
                      }}
                      helperText="When can you start the project at the earliest?"
                    />
                    
                    {/* Add project duration field */}
                    <TextField
                      label="Project Duration (days)"
                      fullWidth
                      type="number"
                      value={projectDuration}
                      onChange={(e) => setProjectDuration(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            days
                          </InputAdornment>
                        ),
                      }}
                      margin="normal"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          '& fieldset': {
                            borderColor: 'rgba(16, 29, 59, 0.2)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(16, 29, 59, 0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#00AB9B',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#00AB9B',
                        },
                      }}
                      helperText="How many days will the project take to complete?"
                    />

                    <TextField
                      label="Bid Notes"
                      fullWidth
                      multiline
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Describe what's included in your bid, your availability, and any special considerations"
                      margin="normal"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          '& fieldset': {
                            borderColor: 'rgba(16, 29, 59, 0.2)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(16, 29, 59, 0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#00AB9B',
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#00AB9B',
                        },
                      }}
                      helperText="Detailed notes help clients understand your offer better (optional)"
                    />

                    {/* Add file attachment uploader */}
                    <Box sx={{ 
                      p: { xs: 2, sm: 2 }, 
                      mt: { xs: 2, sm: 2 },
                      border: '1px solid rgba(0, 171, 155, 0.2)',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(0, 171, 155, 0.03)'
                    }}>
                      <Typography 
                        sx={{ 
                          fontWeight: 600, 
                          mb: 1,
                          color: '#101D3B',
                          fontSize: '0.875rem'
                        }}
                      >
                        Add Attachments (Optional)
                      </Typography>
                      
                      <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'rgba(16, 29, 59, 0.6)' }}>
                        Upload documents, photos, or files related to your bid (max 10 files)
                      </Typography>
                      
                      <FileAttachmentUploader 
                        vendorId={currentVendor?.id || ''}
                        requestId={requestId || ''}
                        onUploadComplete={handleFilesUpload}
                        onError={(error) => setBidError(error.message)}
                        onUploadStatusChange={handleUploadStatusChange}
                        maxFiles={10}
                        autoUpdate={false}
                      />
                      
                      {/* Display warning if uploads are in progress */}
                      {uploadsInProgress && (
                        <Box sx={{ mt: { xs: 2, sm: 2 }, display: 'flex', alignItems: 'center' }}>
                          <CircularProgress size={16} sx={{ mr: 1, color: '#00AB9B' }} />
                          <Typography variant="caption" color="text.secondary">
                            Uploading files... Please wait.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    {/* Insurance requirements confirmation checkbox */}
                    {clientProfile && (
                      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                        <FormControlLabel
                          control={
                            <Checkbox 
                              checked={insuranceConfirmed}
                              onChange={(e) => setInsuranceConfirmed(e.target.checked)}
                              sx={{
                                color: '#00AB9B',
                                '&.Mui-checked': {
                                  color: '#00AB9B',
                                },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              I confirm that my company meets all the insurance requirements listed for this job
                            </Typography>
                          }
                          sx={{ 
                            alignItems: 'flex-start',
                            '& .MuiFormControlLabel-label': { 
                              mt: 0.5
                            }
                          }}
                        />
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 2, mt: { xs: 2, sm: 3 } }}>
                      <Button 
                        variant="contained" 
                        disabled={
                          !bidAmount || 
                          !earliestStartDate ||
                          !projectDuration ||
                          submitBidMutation.isPending || 
                          uploadsInProgress ||
                          bidSubmitted ||
                          !insuranceConfirmed
                        }
                        fullWidth
                        size="large"
                        type="submit"
                        sx={{ 
                          bgcolor: '#00AB9B',
                          color: 'white',
                          borderRadius: '8px',
                          fontWeight: 600,
                          py: 1.5,
                          '&:hover': {
                            bgcolor: '#008f83',
                          },
                          '&.Mui-disabled': {
                            bgcolor: 'rgba(0, 171, 155, 0.3)',
                            color: 'rgba(255, 255, 255, 0.8)'
                          }
                        }}
                      >
                        {submitBidMutation.isPending ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                            Submitting...
                          </>
                        ) : (bidSubmitted ? 'Bid Submitted' : 'Submit Bid')}
                      </Button>
                    </Box>
                    
                    <Box sx={{ mt: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                      <Button 
                        variant="text" 
                        color="error"
                        onClick={handleRejectRequest}
                        disabled={submitBidMutation.isPending}
                        sx={{ 
                          color: '#d32f2f',
                          textTransform: 'none',
                          '&:hover': {
                            bgcolor: 'rgba(211, 47, 47, 0.04)'
                          }
                        }}
                      >
                        Ignore this request
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>
          ) : (
            <Grid item xs={12} md={4}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: { xs: 2, sm: 3 }, 
                  borderRadius: '16px', 
                  border: '1px solid rgba(16, 29, 59, 0.15)',
                  boxShadow: '0px 4px 20px -4px rgba(16, 29, 59, 0.08)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    mb: { xs: 1, sm: 2 },
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <AttachMoneyIcon sx={{ mr: 1, color: '#00AB9B' }} />
                  Interested in this job?
                </Typography>
                
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Sign in to your vendor account to bid on this service request.
                </Typography>
                
                <Button 
                  variant="contained" 
                  fullWidth
                  size="large"
                  onClick={() => navigate('/login', { state: { from: location.pathname } })}
                  sx={{ 
                    bgcolor: '#00AB9B',
                    color: 'white',
                    borderRadius: '8px',
                    fontWeight: 600,
                    py: 1.5,
                    mb: 2,
                    '&:hover': {
                      bgcolor: '#008f83',
                    }
                  }}
                >
                  Sign In
                </Button>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default ServiceRequestDetail; 